import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Brand } from '../components/Brand'
import { teacherApi } from './teacherApi'
import { repository } from '../data/repository'
import { config } from '../domain/config'
import {
  authErrorMessage, clearTeacherSession, loadGoogleIdentityServices, parseCredentialClaims,
  readTeacherSession, saveTeacherSession, type TeacherIdentity, type TeacherSession,
} from './googleIdentity'

interface BootstrapResponse { teacher: TeacherIdentity }
export interface AuthenticatedTeacher {
  session: TeacherSession
  signOut: () => void
  api: <T>(action: string, payload?: Record<string, unknown>) => Promise<T>
}

export function TeacherAuthGate({ children }: { children: (authentication: AuthenticatedTeacher | null) => ReactNode }) {
  const [session, setSession] = useState<TeacherSession | null>(() => config.mode !== 'mock' ? readTeacherSession() : null)
  const [status, setStatus] = useState<'checking' | 'signed-out' | 'authenticated'>(() => config.mode !== 'mock' ? 'checking' : 'authenticated')
  const [error, setError] = useState('')
  const buttonRef = useRef<HTMLDivElement>(null)
  const signOut = () => { repository.setTeacherToken(null); clearTeacherSession(); setSession(null); setError(''); setStatus('signed-out') }

  useEffect(() => {
    if (config.mode === 'mock') return
    if (!config.googleClientId || (config.mode === 'google' && !config.apiUrl)) { setError('Falta completar la configuración Google del frontend.'); setStatus('signed-out'); return }
    if (!session?.idToken) { setStatus('signed-out'); return }
    const validateStoredSession = async () => {
      try {
        parseCredentialClaims(session.idToken, config.googleClientId)
        const result = await teacherApi<BootstrapResponse>('bootstrap', {}, session.idToken)
        repository.setTeacherToken(session.idToken); setSession(saveTeacherSession(session.idToken, result.teacher)); setError(''); setStatus('authenticated')
      } catch (reason) { clearTeacherSession(); setSession(null); setError(authErrorMessage(reason)); setStatus('signed-out') }
    }
    void validateStoredSession()
  // Stored credentials are revalidated once when /master mounts.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (config.mode === 'mock' || status !== 'signed-out' || !buttonRef.current || !config.googleClientId) return
    let active = true
    loadGoogleIdentityServices().then((google) => {
      if (!active || !buttonRef.current) return
      google.accounts.id.initialize({
        client_id: config.googleClientId, auto_select: false, cancel_on_tap_out: true, context: 'signin', ux_mode: 'popup',
        callback: (response) => {
          if (!response.credential) { setError('Google no devolvió una credencial válida.'); return }
          setStatus('checking'); setError('')
          try { parseCredentialClaims(response.credential, config.googleClientId) }
          catch (reason) { clearTeacherSession(); setError(authErrorMessage(reason)); setStatus('signed-out'); return }
          teacherApi<BootstrapResponse>('bootstrap', {}, response.credential).then((result) => {
            repository.setTeacherToken(response.credential!); setSession(saveTeacherSession(response.credential!, result.teacher)); setStatus('authenticated')
          }).catch((reason) => { clearTeacherSession(); setError(authErrorMessage(reason)); setStatus('signed-out') })
        },
      })
      buttonRef.current.replaceChildren()
      google.accounts.id.renderButton(buttonRef.current, {
        type: 'standard', theme: 'outline', size: 'large', text: 'signin_with', shape: 'rectangular', width: 320, logo_alignment: 'left',
      })
    }).catch(() => active && setError('No se pudo cargar Google Identity Services. Revisa la conexión e inténtalo nuevamente.'))
    return () => { active = false }
  }, [status])

  if (config.mode === 'mock') return children(null)
  if (status === 'authenticated' && session) return children({
    session, signOut,
    api: <T,>(action: string, payload: Record<string, unknown> = {}) => teacherApi<T>(action, payload, session.idToken),
  })
  return <main className="auth-page"><section className="auth-card"><Brand /><div className="auth-copy"><p className="eyebrow">Panel profesor</p><h1>{status === 'checking' ? 'Validando acceso…' : 'Bienvenido'}</h1><p>{status === 'checking' ? 'Estamos comprobando tu sesión de Google.' : 'Inicia sesión con una cuenta autorizada para gestionar las asistencias.'}</p></div>{status === 'checking' ? <div className="auth-spinner" role="status" aria-label="Validando sesión" /> : <><div ref={buttonRef} className="google-button" aria-label="Iniciar sesión con Google" /><p className="auth-security">El acceso está limitado a los correos autorizados por HILTAC.</p></>}{error && <p className="notice notice--error" role="alert">{error}</p>}</section></main>
}
